import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Box} from '@material-ui/core';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import {MoreHorizRounded} from '@material-ui/icons';
import DOMPurify from 'dompurify';
import {Base64} from 'js-base64';

import {
  decryptPieceWithMetric,
  initDecrypterWithMetric,
} from '../../api/decryptUtil';
import {zipFiles} from '../../api/fileUtil';
import {readJWT, resolveInlines, unpackData} from '../../api/msgUtil';
import {DEFAULT_MAIL_CONTENT_DOMPURIFY_CONFIG} from '../../api/sanitizeUtil';
import {fetchMessage, getAttachment} from '../../api/zmail';
import {ReactComponent as ZoomLogo} from '../../assets/ZoomLogoWhite.svg';
import AlertBanner from '../AlertBanner/AlertBanner';
import Attachment from '../Attachment/Attachment';
import ErrorDialogBox from '../ErrorDialogBox/ErrorDialogBox';
import ResponsiveIFrame from '../ResponsiveIFrame/ResponsiveIFrame';

import styles, {
  DownloadAllBtnStyles,
  envelopeStyles,
  headerContainer,
  headerContentStyles,
  logoStyles,
  pageContainerStyles,
  quoteTooltipStyles,
  toggleQuoteStyles,
} from './style';

const useStyles = makeStyles(styles);

const PageContainer = withStyles(pageContainerStyles)(Container);
const HeaderContainer = withStyles(headerContainer)(Box);
const HeaderContent = withStyles(headerContentStyles)(Box);
const MessageContainer = withStyles(envelopeStyles)(Box);
const ToggleQuoteButton = withStyles(toggleQuoteStyles)(IconButton);
const ToggleQuoteTooltip = withStyles(quoteTooltipStyles)(Tooltip);

const useLogoStyles = makeStyles(logoStyles);
const useDownloadAllBtnStyles = makeStyles(DownloadAllBtnStyles);
/**
 * ViewMessage Component
 * @param {Object} props
 * @param {Object} props.location
 * @param {string} props.location.search
 * @return {ReactElement}
 */
function ViewMessage(props) {
  const {t} = useTranslation();

  const classes = useStyles();

  const logoClasses = useLogoStyles();
  const downloadAllBtnClasses = useDownloadAllBtnStyles();

  const [msgExpiration, setMsgExpiration] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');

  const [isBadRequest, setIsBadRequest] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [attachmentData, setAttachmentData] = useState([]);
  const [downloadAllAtt, setDownloadAllAtt] = useState(false);

  // const [msgBody, setMsgBody] = useState('');
  const [quoteCollapsed, setQuoteCollapsed] = useState(true);
  const quoteClass = quoteCollapsed ?
    classes.hiddenQuote :
    classes.displayingQuote;
  const quoteTooltip = quoteCollapsed ?
    'Show quoted content' :
    'Hide quoted content';

  const [msgHtmlBody, setMsgHtmlBody] = useState('');
  const [msgHtmlQuote, setMsgHtmlQuote] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [inlineAttachments, setInlineAttachments] = useState([]);
  // a map from attachment cid to its created local blob url and loading status
  const [inlineURLs, setInlineURLs] = useState({});
  const [decrypter, setDecrypter] = useState(null);

  const searchStr = props.location.search.trim();
  const qsParams = useMemo(() => new URLSearchParams(searchStr), [searchStr]);
  const recipient = qsParams.get('rcpt');
  // hide expiration timestamp for healthcheck, it is because we compare
  // snapshot for health check. So we only need create a single snapshot
  // for all clusters if hide the timestamp on UI.
  const hideExp = qsParams.get('hc');

  // Remove the '#' prefix from the hash fragment
  const hashFragment = props.location.hash.trim().slice(1);
  const hashParams = new URLSearchParams(hashFragment);
  const password = hashParams.get('pwd');
  const recipientSigHash = hashParams.get('rshash');

  const fetchMsg = async () => {
    try {
      const params = {
        tok: qsParams.get('tok'),
        dc: qsParams.get('dc'),
        messageId: qsParams.get('id'),
      };
      // fetch message
      const resp = await fetchMessage(params);

      const decrypter = await initDecrypterWithMetric(
          resp.manifest,
          recipient,
          password,
          recipientSigHash,
      );
      setDecrypter(decrypter);

      // decrypt message
      const cryptoMsgBytes = await decryptPieceWithMetric(
          decrypter,
          0,
          Base64.toUint8Array(resp.originalMime),
      );

      const metadata = resp.payload;

      // unpack message
      const unpacked = await unpackData(metadata, cryptoMsgBytes);
      const cleanHtmlBody = DOMPurify.sanitize(unpacked.htmlBody,
          {...DEFAULT_MAIL_CONTENT_DOMPURIFY_CONFIG},
      );
      const cleanHtmlQuote = DOMPurify.sanitize(unpacked.htmlQuote,
          {...DEFAULT_MAIL_CONTENT_DOMPURIFY_CONFIG},
      );
      setFrom(unpacked.from);
      setTo(unpacked.to);
      setCc(unpacked.cc);
      setSubject(unpacked.subject);
      setMsgHtmlBody(cleanHtmlBody);
      setMsgHtmlQuote(cleanHtmlQuote);
      setAttachments(unpacked.attachments);
      setInlineAttachments(unpacked.inlineAttachments);
      // can trust token after server-validated
      const token = readJWT(qsParams.get('tok'));
      // a posix timestamp in seconds
      const expirationPosix = token.payload.exp;
      if (expirationPosix) {
        // convert to Date in milliseconds
        const date = new Date(expirationPosix*1000);
        // Format as locale-aware display date
        const intlDate = new Intl.DateTimeFormat(
            [], {dateStyle: 'medium', timeStyle: 'long'})
            .format(date);
        if (hideExp === null) {
          setMsgExpiration(t('message.msgExpiration', [intlDate]));
        }
      } else {
        setMsgExpiration(t('error.unknownMsgExpiration'));
        setErrorMessage(t('error.couldNotGetMsgExp'));
        setIsBadRequest(true);
      }
    } catch (error) {
      console.log(error);
      if (error.message) {
        if (error.message.includes('400')) {
          setErrorMessage(t('error.errorMsg400'));
        } else if (error.message.includes('401')) {
          setErrorMessage(t('error.errorMsg401'));
        } else if (error.message.includes('404')) {
          setErrorMessage(t('error.errorMsg404'));
        }
      } else {
        setErrorMessage(t('error.errorMsgOther'));
      }
      setIsBadRequest(true);
    }
  };

  const handleDownloadAllAtt = async (e) => {
    e.preventDefault();
    setDownloadAllAtt(true);
    if (attachments.length> 0 && attachmentData.length === attachments.length) {
      zipFiles(attachmentData);
      setDownloadAllAtt(false);
    }
  };

  const addToFileList = useCallback((attData) => {
    setAttachmentData((prevData) => [...prevData, attData]);
  }, []);

  const fetchAndInsertInlineAtt = useCallback(async (att) => {
    // remove outer braces of att cid format < + cidvalue + >
    const cid = att.cid.substr(1, att.cid.length-2);
    const cidSrc = 'cid:' + cid;
    setInlineURLs((prevCidURLMap) => {
      return {
        ...prevCidURLMap,
        [cidSrc]: {
          status: 'LOADING',
          imgUrl: null,
        },
      };
    });
    try {
      const resp = await getAttachment({
        attId: att.id,
        tok: qsParams.get('tok'),
        dc: qsParams.get('dc'),
        messageId: qsParams.get('id'),
      });
      const json = await resp.json();
      const decrypted = await decryptPieceWithMetric(
          decrypter, json.index, Base64.toUint8Array(json.data));
      const imgUrl = URL.createObjectURL(new Blob(
          [decrypted],
          {type: att.mimeType},
      ));
      setInlineURLs((prevCidURLMap) => {
        return {
          ...prevCidURLMap,
          [cidSrc]: {
            status: 'SUCCESS',
            imgUrl: imgUrl,
          },
        };
      });
    } catch (e) {
      console.log('failed to fetch inline att:', att);
      setInlineURLs((prevCidURLMap) => {
        return {
          ...prevCidURLMap,
          [cidSrc]: {
            status: 'ERROR',
            imgUrl: null,
          },
        };
      });
    }
  }, [decrypter, qsParams]);

  useEffect(() => {
    if (inlineAttachments.length) {
      inlineAttachments.forEach(fetchAndInsertInlineAtt);
    }
  }, [inlineAttachments, qsParams, decrypter, fetchAndInsertInlineAtt]);

  useEffect(() => {
    if (msgHtmlBody) {
      setMsgHtmlBody((prevMsgHtmlBody) =>
        resolveInlines(prevMsgHtmlBody, inlineURLs));
    }
    if (msgHtmlQuote) {
      setMsgHtmlQuote((prevMsgHtmlQuote) =>
        resolveInlines(prevMsgHtmlQuote, inlineURLs));
    }
  }, [inlineURLs, msgHtmlBody, msgHtmlQuote]);

  useEffect(() => {
    if (attachments.length> 0 && attachmentData.length === attachments.length) {
      setDownloadAllAtt(false);
      if (downloadAllAtt) {
        zipFiles(attachmentData);
      }
    }
  }, [attachmentData, attachments.length, downloadAllAtt]);

  // useEffect to run only after init render and after unpdate on
  // url changes
  useEffect(() => {
    fetchMsg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStr]);

  return (
    <React.Fragment>
      <CssBaseline />
      <PageContainer component="main" maxWidth="lg">
        <HeaderContainer>
          <HeaderContent>
            <ZoomLogo className={logoClasses.root} />
          </HeaderContent>
        </HeaderContainer>
        <MessageContainer>
          {(msgExpiration) &&
              <AlertBanner
                alertType="warning"
                message={msgExpiration}
              />
          }
          <div className={classes.titleline}>
            <div className={classes.title}>{t('message.from')}</div>
            {from}
          </div>
          <Divider />
          <div className={classes.titleline}>
            <div className={classes.title}>{t('message.to')}</div>
            {to}
          </div>
          {(cc) && (
            <>
              <Divider />
              <div className={classes.titleline}>
                <div className={classes.title}>{t('message.cc')}</div>
                {cc}
              </div>
            </>
          )}

          <Divider />
          <div className={classes.titleline}>
            <div className={classes.title}>{t('message.subject')}</div>
            {subject}
          </div>
          <Divider />
          {/* <div className={classes.bodyline}>{msgBody}</div>
          <Divider /> */}
          <div className={classes.bodyline}>
            <ResponsiveIFrame
              title="message body"
              id="iFrameMsgBody"
              srcDoc={msgHtmlBody}
            />
            {msgHtmlQuote && (
              <>
                <ToggleQuoteTooltip
                  title={quoteTooltip}
                  placement="right"
                >
                  <ToggleQuoteButton
                    aria-label={quoteTooltip}
                    aria-expanded={!quoteCollapsed}
                    disableRipple
                    onClick={() => setQuoteCollapsed(!quoteCollapsed)}
                  >
                    <MoreHorizRounded />
                  </ToggleQuoteButton>
                </ToggleQuoteTooltip>

                <div
                  className={quoteClass}
                >
                  <ResponsiveIFrame
                    title="message quote"
                    id="iFrameMsgQuote"
                    srcDoc={msgHtmlQuote}
                  />
                </div>
              </>
            )}
          </div>
          {attachments.length > 0 && (
            <>
              <Divider />
              {attachments.length > 0 && (
                <div className={classes.titleline}>
                  <b>
                    {attachments.length}{' '}
                    {attachments.length > 1 ?
                      t('message.attachements') :
                      t('message.attachement')
                    }
                  </b>
                  {attachments.length > 1 && (
                    <button className={downloadAllBtnClasses.root}
                      onClick={handleDownloadAllAtt}>
                      {t('message.downloadAll')}
                    </button>
                  )}
                </div>
              )}
              <div style={{
                // maxWidth: 'fit-content',
              }}>
                {attachments.map((att) => (
                  <Attachment
                    key={att.id}
                    attachment={att}
                    searchStr={searchStr}
                    decrypter={decrypter}
                    downloadAllAtt={downloadAllAtt}
                    addToFileList={addToFileList}
                  />
                ))}
              </div>
            </>
          )}
        </MessageContainer>

        <ErrorDialogBox
          open={isBadRequest}
          message={errorMessage}
          onHandleClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              setIsBadRequest(false);
            }
          }}
        />
      </PageContainer>
    </React.Fragment>
  );
}

export default ViewMessage;
